<template>
  <lf-modal :title="modalTitle" title-border :close="close">
    <template #content>
      <div class="flex w-full flex-col space-y-4 px-6 pt-6">
        <div
          v-for="utmType in UTM_TYPES"
          :key="utmType"
          class="flex w-full items-center mb-4"
        >
          <label class="w-32 shrink-0 text-sm font-medium text-label">
            {{ camelCaseToWords(utmType) }}:
          </label>
          <lf-input
            :placeholder="$t('DEALS.TRACKING.UTM_VALUE')"
            :value="formValues[utmType as UtmTypeKey]"
            :name="utmType"
            :show-label="false"
            maxlength="255"
            :data-cy="`utm-${utmType}-input`"
            class="h-10 flex-1 mb-0!"
            @key-released="updateFormValue(utmType as UtmTypeKey, $event)"
          />
        </div>
      </div>
      <hr />
      <div class="flex w-full justify-end space-x-4 p-6">
        <outline-button
          v-if="isEditMode"
          type="submit"
          data-cy="delete-utm-button"
          class="cursor-pointer"
          :disabled="isSubmittingOrDeleting"
          @click="deleteUTMSet"
        >
          {{ $t("COMMON.DELETE") }}
        </outline-button>
        <primary-button
          type="submit"
          data-cy="save-utm-button"
          class="cursor-pointer"
          :disabled="isSaveDisabled"
          @click="saveUTMSet"
        >
          {{ isEditMode ? $t("COMMON.SAVE") : $t("COMMON.ADD") }}
        </primary-button>
      </div>
    </template>
  </lf-modal>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { UTM_TYPES } from "@/helpers/constants/tracking";
import { camelCaseToWords } from "@/helpers/formatting";
import { usePromiseWrapper } from "@/hooks/common";

import LfModal from "@/components/ui/Modal.vue";

import type { UtmParameterSet } from "@/models/applications";

type UtmTypeKey = Exclude<keyof UtmParameterSet, "id">;

const { set, close } = defineProps<{
  set?: UtmParameterSet;
  close: () => void;
}>();

const { dispatch } = useStore();
const { t } = useI18n();

const formValues = reactive<Record<UtmTypeKey, string>>({
  utm_source: set?.utm_source || "",
  utm_medium: set?.utm_medium || "",
  utm_campaign: set?.utm_campaign || "",
  utm_term: set?.utm_term || "",
  utm_content: set?.utm_content || "",
});

const modalTitle = computed(() => {
  return isEditMode.value
    ? `${t("COMMON.EDIT")} ${t("DEALS.TRACKING.UTM_PARAMETER", 2)}`
    : `${t("COMMON.ADD")} ${t("DEALS.TRACKING.UTM_PARAMETER", 2)}`;
});

const isEditMode = computed(() => {
  return !!set;
});

const isSubmittingOrDeleting = computed(
  () => isSubmitting.value || isDeleting.value
);

const hasAnyValue = computed(() => {
  return Object.values(formValues).some((value) => value.trim() !== "");
});

const isSaveDisabled = computed(() => {
  return isSubmittingOrDeleting.value || !hasAnyValue.value;
});

const updateFormValue = (key: UtmTypeKey, value: string) => {
  formValues[key] = value;
};

const { fetchWrapper: deleteUTMSet, loading: isDeleting } = usePromiseWrapper(
  async () => {
    if (set) {
      await dispatch("applications/deleteUTMParameterSet", set.id);
    }
    close();
  }
);

const { fetchWrapper: saveUTMSet, loading: isSubmitting } = usePromiseWrapper(
  async () => {
    if (!hasAnyValue.value) {
      return;
    }

    await dispatch("applications/saveUTMParameterSet", {
      ...(set?.id && { id: set.id }),
      ...formValues,
    });
    close();
  }
);
</script>
